import { z } from 'zod';
import type { IsoDate } from './dates';
import type { ContractKind, RateUnit } from './enums';
import { ACT_STATUSES, REGISTER_STATUSES } from './enums';
import type { Minor } from './money';
import { idSchema } from './schemas';
import { monthSchema } from './calendar';

// ───────────── Правило 12.4.6: розрахунок винагороди ─────────────

export type RemunerationBasis = 'timesheet' | 'reports';

export interface RemunerationInput {
  contractKind: ContractKind;
  rate: Minor;
  rateUnit: RateUnit;
  /** Планові години за місяць у межах призначення (з урахуванням FTE). */
  plannedHours: number;
  /** Фактичні години з табеля за проєктом договору. */
  timesheetHours: number;
  /** Погоджені години звітів про послуги. */
  reportHours: number;
  /** Погоджені одиниці послуг (консультації, сесії…). */
  reportQuantity: number;
  /** Гранична сума договору і скільки вже нараховано раніше. */
  totalCap?: Minor | null;
  accruedBefore?: Minor;
}

export interface RemunerationResult {
  amount: Minor;
  basis: RemunerationBasis;
  hours: number;
  quantity: number;
  /** Сума обмежена граничною сумою договору. */
  capped: boolean;
  /** Пояснення розрахунку для реєстру. */
  formula: string;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Трудовий договір рахується з табеля, ЦПД і ФОП — з погоджених звітів.
 * Місячна ставка — пропорційно до відпрацьованих годин (не більше 100 %);
 * погодинна — ставка × години; за послугу — ставка × кількість одиниць.
 * Податки й утримання рахує бухгалтерія (сума — до утримань).
 */
export function computeRemuneration(input: RemunerationInput): RemunerationResult {
  const basis: RemunerationBasis = input.contractKind === 'employment' ? 'timesheet' : 'reports';
  const hours = round2(basis === 'timesheet' ? input.timesheetHours : input.reportHours);
  const quantity = basis === 'reports' ? input.reportQuantity : 0;
  let amount: number;
  let formula: string;
  if (input.rateUnit === 'month') {
    const share = input.plannedHours > 0 ? Math.min(1, hours / input.plannedHours) : 0;
    amount = Math.round(input.rate * share);
    formula = `ставка × ${hours}/${input.plannedHours} год`;
  } else if (input.rateUnit === 'hour') {
    amount = Math.round(input.rate * hours);
    formula = `ставка × ${hours} год`;
  } else {
    amount = Math.round(input.rate * quantity);
    formula = `ставка × ${quantity} од.`;
  }
  let capped = false;
  if (input.totalCap !== null && input.totalCap !== undefined) {
    const left = Math.max(0, input.totalCap - (input.accruedBefore ?? 0));
    if (amount > left) {
      amount = left;
      capped = true;
      formula += ' (обмежено граничною сумою договору)';
    }
  }
  return { amount, basis, hours, quantity, capped, formula };
}

export interface ReconciliationIssue {
  date: IsoDate;
  timesheetHours: number | null;
  reportHours: number;
}

/**
 * Звірка табеля і звітів: дні, де години звітів перевищують табель
 * (або табеля за день немає взагалі).
 */
export function reconcile(
  timesheetByDate: Map<IsoDate, number>,
  reportsByDate: Map<IsoDate, number>,
  timesheetFilled: boolean,
): ReconciliationIssue[] {
  if (!timesheetFilled) return [];
  const issues: ReconciliationIssue[] = [];
  for (const [date, reportHours] of reportsByDate) {
    const ts = timesheetByDate.get(date) ?? null;
    if (ts === null || Math.round(reportHours * 100) > Math.round(ts * 100)) {
      issues.push({ date, timesheetHours: ts, reportHours: round2(reportHours) });
    }
  }
  return issues.sort((a, b) => a.date.localeCompare(b.date));
}

/** Номер акта: «АКТ-2026/0042». */
export function formatActNumber(year: number, seq: number): string {
  return `АКТ-${year}/${String(seq).padStart(4, '0')}`;
}

// ───────────── Схеми ─────────────

export const actsPreviewSchema = z.object({ month: monthSchema, projectId: idSchema.optional() });
export const actsGenerateSchema = actsPreviewSchema.extend({
  contractIds: z.array(idSchema).min(1).max(500),
});
export const actStatusSchema = z.object({
  id: idSchema,
  status: z.enum(ACT_STATUSES),
});
export const actListSchema = z.object({
  month: monthSchema.optional(),
  projectId: idSchema.optional(),
  status: z.enum(ACT_STATUSES).optional(),
});

export const registerBuildSchema = z.object({ month: monthSchema, projectId: idSchema });
export const registerStatusSchema = z.object({ id: idSchema, status: z.enum(REGISTER_STATUSES) });
export const registerLinePaidSchema = z.object({ id: idSchema, paid: z.boolean() });
